/**
 * REST client.
 *
 * The types here mirror the server records rather than importing them: the
 * server is compiled on its own and does not share a package with the client,
 * so the duplication is the price of keeping the two builds independent. The
 * shapes are small and stable enough that this is the cheaper trade.
 */

export interface Organization {
  id: string;
  name: string;
  color: string;
  createdAt: number;
  updatedAt: number;
  /** Present on list responses only. */
  boardCount?: number;
  eventCount?: number;
  onlineUsers?: number;
  /** Caller's membership role, or `null` for a super admin viewing an org they have not joined. */
  myRole?: 'admin' | 'member' | null;
  /** Caller's pedagogical profile in this org (tutoring layer), or `null` if unset. */
  myKind?: 'teacher' | 'curator' | 'student' | null;
}

/** Access level on one board, decided by the server and never by the URL. */
export type BoardRoleName = 'editor' | 'commenter' | 'viewer';

export interface BoardSummary {
  id: string;
  name: string;
  orgId: string;
  /** Account that created it; `null` for a board owned by the organisation. */
  createdBy: string | null;
  /** Visual grouping, or `null` for «Без блока». */
  blockId: string | null;
  /** Tutoring group this board belongs to, when it is a classroom board. */
  groupId?: string | null;
  createdAt: number;
  updatedAt: number;
  thumbnail: string | null;
  /** Live participants, present on list responses. */
  participants?: number;
  /** What this viewer may do — computed server-side by the permission matrix. */
  myRole?: BoardRoleName | null;
  canDelete?: boolean;
  canRename?: boolean;
  canMove?: boolean;
  canManageMembers?: boolean;
}

export interface BoardDetail extends BoardSummary {
  myRole: BoardRoleName | null;
  canDelete: boolean;
  canRename: boolean;
  canMove: boolean;
  canManageMembers: boolean;
  connections?: number;
  nodeCount?: number;
}

export interface BoardBlock {
  id: string;
  orgId: string;
  name: string;
  order: number;
  createdAt: number;
}

export interface BoardMemberView {
  userId: string;
  role: BoardRoleName;
  addedAt: number;
  name: string;
  email: string;
  color: string;
}

export interface BoardInvite {
  token: string;
  role: BoardRoleName;
  createdAt: number;
}

export interface BoardAccessView {
  creator: { userId: string; name: string; email: string; color: string } | null;
  canManage: boolean;
  members: BoardMemberView[];
  bans: BoardBanView[];
  invites: BoardInvite[];
}

export interface BoardBanView {
  userId: string;
  bannedAt: number;
  name: string;
  email: string;
  color: string;
}

/** One journal entry. Names are snapshots taken when the action happened. */
export interface AuditEntry {
  id: string;
  at: number;
  actorId: string | null;
  actorName: string;
  actorEmail: string;
  orgId: string | null;
  orgName: string;
  boardId: string | null;
  boardName: string;
  action: string;
  meta?: Record<string, string | number>;
}

export interface AuditQuery {
  orgId?: string;
  boardId?: string;
  actorId?: string;
  action?: string;
  before?: number;
  limit?: number;
}

export interface EventTask {
  id: string;
  text: string;
  done: boolean;
}

export interface CalendarEvent {
  id: string;
  orgId: string;
  boardId: string | null;
  title: string;
  description: string;
  /** ISO 8601, UTC. */
  start: string;
  end: string;
  allDay: boolean;
  tasks: EventTask[];
  remindMinutesBefore: number | null;
  color: string;
  /** Account that added it; null for events written before events had one. */
  createdBy: string | null;
  createdByName: string | null;
  done: boolean;
  doneAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface EventInput {
  orgId: string;
  title: string;
  description: string;
  start: string;
  end: string;
  allDay: boolean;
  boardId: string | null;
  tasks: { id?: string; text: string; done: boolean }[];
  remindMinutesBefore: number | null;
  color: string;
  done?: boolean;
}

export interface BoardSnapshot {
  id: string;
  boardId: string;
  label: string;
  createdAt: number;
  sizeBytes: number;
}

export interface CallRecord {
  id: string;
  boardId: string;
  startedBy: string;
  startedByName: string;
  startedAt: number;
  endedAt: number | null;
}

export interface ChatMuteView {
  userId: string;
  mutedBy: string;
  mutedByName: string;
  until: number | null;
  createdAt: number;
}

export interface CallStatus {
  /** False when the server has no LiveKit deployment to talk to. */
  configured: boolean;
  call: CallRecord | null;
  /** How many people LiveKit currently sees in the room (0 if empty / unknown). */
  participantCount: number;
  chatMute: ChatMuteView | null;
  chatMutes: ChatMuteView[];
  canModerate: boolean;
}

export interface CallToken {
  token: string;
  url: string;
  roomName: string;
  canPublish: boolean;
  /** The caller's role in the board's organisation, as the server sees it. */
  roleSlug: string | null;
  chatMuted: boolean;
  /** A moderator turned this participant's microphone off; it stays off on rejoin. */
  micMuted?: boolean;
  canModerate: boolean;
}

/** Someone kept out of a board's call by a kick or a ban. */
export interface CallBanView {
  userId: string;
  name: string;
  color: string;
  reason: 'kick' | 'ban' | 'mute';
  until: number | null;
}

export interface UploadedAsset {
  id: string;
  url: string;
  width: number;
  height: number;
  mime: string;
  bytes: number;
}

const BASE = '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    /** The parsed JSON error body, for details beyond the code (e.g. `until`). */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const UNREACHABLE = 'unreachable';

function unreachableError(): ApiError {
  return new ApiError(
    'Сервер API не запущен. В корне проекта выполните npm run dev — нужны оба процесса: web и server.',
    0,
    UNREACHABLE,
  );
}

/** Exported so sibling REST clients (`authApi.ts`) share the same error handling. */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Fastify's JSON parser rejects a body-less request that still carries a
  // `content-type: application/json` header (`FST_ERR_CTP_EMPTY_JSON_BODY`),
  // so the header is only added when there is an actual JSON body to parse —
  // most often true, but not for a bare DELETE or this file's own
  // restore-snapshot call.
  const hasJsonBody = init?.body !== undefined && !(init.body instanceof FormData);
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        ...(hasJsonBody ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw unreachableError();
  }

  if (!res.ok) {
    // The server answers with a machine-readable `error` code; surfacing it
    // lets callers distinguish "cannot delete the last organisation" from a
    // genuine failure without parsing prose.
    let code: string | undefined;
    let body = '';
    let parsed: unknown;
    try {
      body = await res.text();
      parsed = JSON.parse(body);
      code = (parsed as { error?: string }).error;
    } catch {
      // Not JSON; the text body is the best message available.
    }
    // Vite's proxy returns 500 with an empty or "proxy" body when the API
    // process on :8787 is down — that is not a board error, it is ops.
    if (res.status >= 500 && (!body.trim() || /ECONNREFUSED|proxy/i.test(body))) {
      throw unreachableError();
    }
    throw new ApiError(
      `${init?.method ?? 'GET'} ${path} → ${res.status} ${code ?? body}`,
      res.status,
      code,
      parsed,
    );
  }
  return (await res.json()) as T;
}

const json = (body: unknown): RequestInit['body'] => JSON.stringify(body);

export const api = {
  // ---- organisations ----------------------------------------------------
  listOrgs: () => request<Organization[]>('/orgs'),
  /** Super-admin only; `ownerId` installs someone else as the org's admin. */
  createOrg: (name: string, ownerId?: string) =>
    request<Organization>('/orgs', { method: 'POST', body: json({ name, ownerId }) }),
  updateOrg: (id: string, patch: { name?: string; color?: string }) =>
    request<Organization>(`/orgs/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteOrg: (id: string) => request<{ ok: true }>(`/orgs/${id}`, { method: 'DELETE' }),

  // ---- boards ------------------------------------------------------------
  listBoards: (orgId?: string) =>
    request<BoardSummary[]>(orgId ? `/boards?orgId=${encodeURIComponent(orgId)}` : '/boards'),
  getBoard: (id: string) => request<BoardDetail>(`/boards/${id}`),
  createBoard: (name: string, orgId: string) =>
    request<BoardSummary>('/boards', { method: 'POST', body: json({ name, orgId }) }),
  updateBoard: (
    id: string,
    patch: { name?: string; orgId?: string; thumbnail?: string; blockId?: string | null },
  ) => request<BoardSummary>(`/boards/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteBoard: (id: string) => request<{ ok: true }>(`/boards/${id}`, { method: 'DELETE' }),

  // ---- board access ------------------------------------------------------
  boardAccess: (boardId: string) => request<BoardAccessView>(`/boards/${boardId}/members`),
  addBoardMember: (boardId: string, userId: string, role: BoardRoleName) =>
    request<BoardMemberView>(`/boards/${boardId}/members`, {
      method: 'POST',
      body: json({ userId, role }),
    }),
  removeBoardMember: (boardId: string, userId: string) =>
    request<{ ok: true }>(`/boards/${boardId}/members/${userId}`, { method: 'DELETE' }),
  banBoardMember: (boardId: string, userId: string) =>
    request<BoardBanView>(`/boards/${boardId}/bans/${userId}`, { method: 'POST' }),
  unbanBoardMember: (boardId: string, userId: string) =>
    request<{ ok: true }>(`/boards/${boardId}/bans/${userId}`, { method: 'DELETE' }),
  createBoardInvite: (boardId: string, role: BoardRoleName) =>
    request<BoardInvite>(`/boards/${boardId}/invites`, { method: 'POST', body: json({ role }) }),
  revokeBoardInvite: (boardId: string, token: string) =>
    request<{ ok: true }>(`/boards/${boardId}/invites/${token}`, { method: 'DELETE' }),

  // ---- board blocks ------------------------------------------------------
  listBlocks: (orgId: string) => request<BoardBlock[]>(`/orgs/${orgId}/blocks`),
  createBlock: (orgId: string, name: string) =>
    request<BoardBlock>(`/orgs/${orgId}/blocks`, { method: 'POST', body: json({ name }) }),
  updateBlock: (orgId: string, blockId: string, patch: { name?: string; order?: number }) =>
    request<BoardBlock>(`/orgs/${orgId}/blocks/${blockId}`, {
      method: 'PATCH',
      body: json(patch),
    }),
  deleteBlock: (orgId: string, blockId: string) =>
    request<{ ok: true }>(`/orgs/${orgId}/blocks/${blockId}`, { method: 'DELETE' }),

  // ---- audit journal -----------------------------------------------------

  /**
   * The journal. `orgId` picks the org-scoped endpoint, which an org admin may
   * read; without it this is the server-wide view and needs a super admin.
   */
  listAudit: (query: AuditQuery = {}) => {
    const params = new URLSearchParams();
    if (query.boardId) params.set('boardId', query.boardId);
    if (query.actorId) params.set('actorId', query.actorId);
    if (query.action) params.set('action', query.action);
    if (query.before !== undefined) params.set('before', String(query.before));
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return query.orgId
      ? request<AuditEntry[]>(`/orgs/${query.orgId}/audit${suffix}`)
      : request<AuditEntry[]>(`/admin/audit${suffix}`);
  },

  // ---- version history ---------------------------------------------------
  listSnapshots: (boardId: string) => request<BoardSnapshot[]>(`/boards/${boardId}/snapshots`),
  createSnapshot: (boardId: string, label?: string) =>
    request<BoardSnapshot>(`/boards/${boardId}/snapshots`, {
      method: 'POST',
      body: json({ label }),
    }),
  deleteSnapshot: (boardId: string, snapshotId: string) =>
    request<{ ok: true }>(`/boards/${boardId}/snapshots/${snapshotId}`, { method: 'DELETE' }),
  restoreSnapshot: (boardId: string, snapshotId: string) =>
    request<{ ok: true }>(`/boards/${boardId}/snapshots/${snapshotId}/restore`, { method: 'POST' }),

  // ---- calls ---------------------------------------------------------------
  callStatus: (boardId: string) => request<CallStatus>(`/boards/${boardId}/call`),
  startCall: (boardId: string) =>
    request<CallRecord>(`/boards/${boardId}/call/start`, { method: 'POST' }),
  stopCall: (boardId: string) =>
    request<{ ok: true }>(`/boards/${boardId}/call/stop`, { method: 'POST' }),
  callToken: (boardId: string) =>
    request<CallToken>(`/boards/${boardId}/call/token`, { method: 'POST' }),
  muteChat: (boardId: string, userId: string, durationMinutes?: number | null) =>
    request<ChatMuteView>(`/boards/${boardId}/chat/mutes/${userId}`, {
      method: 'POST',
      body: json({ durationMinutes: durationMinutes ?? null }),
    }),
  unmuteChat: (boardId: string, userId: string) =>
    request<{ ok: true }>(`/boards/${boardId}/chat/mutes/${userId}`, { method: 'DELETE' }),
  muteCallParticipant: (boardId: string, userId: string, muted = true) =>
    request<{ ok: true; muted: boolean }>(`/boards/${boardId}/call/participants/${userId}/mute`, {
      method: 'POST',
      body: json({ muted }),
    }),
  banFromCall: (boardId: string, userId: string, durationMinutes?: number | null) =>
    request<{ ok: true; until: number | null }>(
      `/boards/${boardId}/call/participants/${userId}/ban`,
      { method: 'POST', body: json({ durationMinutes: durationMinutes ?? null }) },
    ),
  listCallBans: (boardId: string) => request<CallBanView[]>(`/boards/${boardId}/call/bans`),
  unbanFromCall: (boardId: string, userId: string) =>
    request<{ ok: true }>(`/boards/${boardId}/call/bans/${userId}`, { method: 'DELETE' }),
  kickCallParticipant: (boardId: string, userId: string) =>
    request<{ ok: true }>(`/boards/${boardId}/call/participants/${userId}/kick`, {
      method: 'POST',
    }),

  // ---- calendar ----------------------------------------------------------
  listEvents: (orgId: string, from?: string, to?: string) => {
    const params = new URLSearchParams({ orgId });
    if (from && to) {
      params.set('from', from);
      params.set('to', to);
    }
    return request<CalendarEvent[]>(`/events?${params.toString()}`);
  },
  createEvent: (input: EventInput) =>
    request<CalendarEvent>('/events', { method: 'POST', body: json(input) }),
  updateEvent: (id: string, patch: Partial<EventInput>) =>
    request<CalendarEvent>(`/events/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteEvent: (id: string) => request<{ ok: true }>(`/events/${id}`, { method: 'DELETE' }),

  // ---- assets ------------------------------------------------------------

  /**
   * Uploads a pasted or dropped file and returns a stable URL.
   *
   * Falls back to a data URL when the server is unreachable so an offline
   * board still shows the image; that copy is local-only and the node will
   * show a placeholder for other peers, which is the honest behaviour.
   */
  async uploadAsset(file: File): Promise<UploadedAsset> {
    const form = new FormData();
    form.append('file', file);
    try {
      return await request<UploadedAsset>('/assets', { method: 'POST', body: form });
    } catch (error) {
      // Only an unreachable dev server falls back to inlining the file. A
      // rejected upload (too big, wrong type, signed out) must not smuggle a
      // multi-MB data URL into the shared doc — past the socket's payload
      // limit that stops the board syncing altogether.
      if (!(error instanceof ApiError && error.code === UNREACHABLE)) throw error;
      if (file.size > 2 * 1024 * 1024) throw error;
      const dataUrl = await fileToDataUrl(file);
      const size = await imageSize(dataUrl);
      return {
        id: `local_${Date.now()}`,
        url: dataUrl,
        width: size.width,
        height: size.height,
        mime: file.type,
        bytes: file.size,
      };
    }
  },
};

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

export function imageSize(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 400, height: 300 });
    img.src = src;
  });
}
